import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { movies } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import ShareModal from '@/pages/detail/components/ShareModal';

export default function Detail() {
  const { id = '' } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [fav, setFav] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [notice, setNotice] = useState('');

  useDocumentTitle(movie ? `${movie.title} - 影视详情 - 蜜桃网` : '影视详情 - 蜜桃网', {
    description: movie
      ? `${movie.title}（${movie.year}）${movie.genres.join('、')}作品，由${movie.director}执导，${movie.actors.join('、')}主演。${movie.synopsis}`
      : '蜜桃网影视详情页，查看影片资料与相关推荐。',
    keywords: movie ? `蜜桃网,${movie.title},${movie.genres.join(',')},影视详情,影视资源` : '蜜桃网,影视详情',
  });

  useEffect(() => {
    if (movie) setFav(isFavorite(movie.id));
  }, [isFavorite, movie]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(''), 2200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  if (!movie) {
    return (
      <div className="mx-auto w-full px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="内容不存在或已下架"
          description="你访问的影视资源可能已被移除，去首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE?.('/');
          }}
        />
      </div>
    );
  }

  const related = movies
    .filter((m) => m.id !== movie.id && (m.type === movie.type || m.genres.some((g) => movie.genres.includes(g))))
    .slice(0, 4);

  const handleFavorite = () => {
    toggleFavorite(movie.id);
    setFav((v) => !v);
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-700">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/category/${movie.type}`} className="transition-colors hover:text-primary-700">
          {movie.type}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{movie.title}</span>
      </nav>

      <section className="grid grid-cols-1 gap-6 md:grid-cols-[280px_1fr] md:gap-10">
        <div className="mx-auto w-48 overflow-hidden rounded-lg border border-background-200/70 md:mx-0 md:w-full">
          <div className="w-full" style={{ aspectRatio: '2 / 3' }}>
            <img
              src={movie.poster}
              alt={`${movie.title}海报`}
              title={`${movie.title} - 蜜桃网影视详情`}
              className="h-full w-full object-top"
            />
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
              {movie.type}
            </span>
            {movie.genres.map((g) => (
              <span key={g} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                {g}
              </span>
            ))}
          </div>

          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{movie.title}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1 text-lg font-bold text-accent-600">
              <i className="ri-star-fill text-base text-primary-500" />
              {movie.rating}
            </span>
            <span className="text-sm text-foreground-500">评分</span>
            <span className="h-4 w-px bg-background-300" />
            <span className="text-sm text-foreground-600">{movie.region}</span>
            <span className="h-4 w-px bg-background-300" />
            <span className="text-sm text-foreground-600">{movie.year}</span>
            <span className="h-4 w-px bg-background-300" />
            <span className="text-sm text-foreground-600">{movie.duration}</span>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-3 rounded-lg border border-background-200/70 bg-background-100 p-5 text-sm sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">导演：</dt>
              <dd className="text-foreground-800">{movie.director}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">上映年份：</dt>
              <dd className="text-foreground-800">{movie.year} 年</dd>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <dt className="shrink-0 text-foreground-500">主演：</dt>
              <dd className="text-foreground-800">{movie.actors.join('、')}</dd>
            </div>
          </dl>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                fav
                  ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              <i className={fav ? 'ri-heart-fill' : 'ri-heart-line'} />
              {fav ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-share-forward-line" />
              分享
            </button>
            <button
              type="button"
              onClick={() => setNotice('演示站点暂未提供在线播放服务，敬请期待。')}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-play-circle-line" />
              播放
            </button>
            <button
              type="button"
              onClick={() => setNotice('演示站点暂未提供下载服务，感谢理解。')}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-download-line" />
              下载
            </button>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-6 w-1.5 rounded-full bg-gradient-to-b from-primary-400 to-accent-500" />
          剧情简介
        </h2>
        <p className="mt-4 max-w-4xl text-sm leading-loose text-foreground-700 md:text-base">{movie.synopsis}</p>
        <p className="mt-4 text-sm text-foreground-500">
          所属分类：
          <Link to={`/category/${movie.type}`} className="ml-1 text-primary-700 hover:text-accent-600">
            {movie.type}
          </Link>
        </p>
      </section>

      <section className="mt-12" data-product-shop>
        <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-6 w-1.5 rounded-full bg-gradient-to-b from-primary-400 to-accent-500" />
          相关推荐
        </h2>
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {related.map((m) => (
            <MovieCard key={m.id} movie={m} />
          ))}
        </div>
      </section>

      {shareOpen && <ShareModal title={movie.title} onClose={() => setShareOpen(false)} />}

      {notice && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50 animate-fade-up">
          {notice}
        </div>
      )}
    </div>
  );
}